import {
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Tooltip,
} from "@mui/material";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import CloseIcon from "@mui/icons-material/Close";
import { DateTime } from "luxon";

import { GlobalErrorMessage } from "features/core/GlobalErrorMessage";

import { useGetRunsQuery } from "services/streamingApi/jobModerator.api";
import { JobRunInformation } from "types/JobRunInformation";

export interface JobRunsDialogProps {
    onClose: () => void;
}

const columns: GridColDef<JobRunInformation>[] = [
    {
        field: "jobName",
        headerName: "Job Name",
        minWidth: 200,
        flex: 1,
        renderCell: (params: GridRenderCellParams<JobRunInformation>) => {
            if (!params.row) {
                return null;
            }

            return (
                <Tooltip
                    title={`Run interval: ${params.row.jobSource.runInterval} | Max run time: ${params.row.jobSource.maxRunTime} | Approval assignee: ${params.row.jobSource.approvalAssignee} | Approval timeout: ${params.row.jobSource.approvalTimeout}`}
                >
                    {params.value}
                </Tooltip>
            );
        },
    },
    {
        field: "created",
        headerName: "Created",
        minWidth: 150,
        type: "dateTime",
        valueFormatter: (value) =>
            value && DateTime.fromISO(value).toRelative(),
    },
    {
        field: "status",
        headerName: "Status",
        minWidth: 150,
        renderCell: (params: GridRenderCellParams<JobRunInformation>) => {
            if (!params.row) {
                return null;
            }

            return (
                <Tooltip title={params.row.errorMessage}>
                    {params.value}
                </Tooltip>
            );
        },
    },
    {
        field: "completedDateTime",
        headerName: "Completed",
        minWidth: 150,
        type: "dateTime",
        valueFormatter: (value) =>
            value && DateTime.fromISO(value).toRelative(),
    },
    {
        field: "nextRun",
        headerName: "Next Run",
        minWidth: 150,
        type: "dateTime",
        valueFormatter: (value) => {
            if (!value) {
                return "";
            }

            const nextRun = DateTime.fromISO(value);

            if (nextRun.diffNow().as("seconds") <= 0) {
                return "Due";
            }

            return nextRun.toRelative();
        },
    },
];

export function JobRunsDialog(props: JobRunsDialogProps) {
    const { data, isLoading, isError, error } = useGetRunsQuery(undefined, {
        skip: false,
        pollingInterval: 5000,
        refetchOnMountOrArgChange: true,
    });

    return (
        <Dialog
            open={true}
            fullWidth={true}
            maxWidth={"lg"}
            onClose={props.onClose}
        >
            <DialogTitle>Latest job runs</DialogTitle>
            <IconButton
                aria-label="close"
                onClick={props.onClose}
                sx={(theme) => ({
                    position: "absolute",
                    right: 8,
                    top: 8,
                    color: theme.palette.grey[500],
                })}
            >
                <CloseIcon />
            </IconButton>

            <DialogContent>
                <DataGrid
                    density={"standard"}
                    rows={data}
                    columns={columns}
                    loading={isLoading}
                    slotProps={{
                        loadingOverlay: {
                            variant: "linear-progress",
                            noRowsVariant: "skeleton",
                        },
                        toolbar: {
                            showQuickFilter: false,
                        },
                    }}
                    localeText={{
                        noResultsOverlayLabel: "No job runs found",
                    }}
                    initialState={{
                        sorting: {
                            sortModel: [{ field: "jobName", sort: "asc" }],
                        },
                    }}
                />

                {isError && (
                    <GlobalErrorMessage
                        title={"Unable to fetch job runs"}
                        message={"Please contact the service desk."}
                        debugData={{ error }}
                        alertSeverity={"error"}
                    />
                )}
            </DialogContent>
        </Dialog>
    );
}
